import { IContextFileTag } from "models/IContextFileTag";
import { IEngagementSourceDocument } from "apps/project-iq/models/IEngagementSourceDocument";

export const ENGAGEMENT_DOCUMENT_TAG_IDS: readonly string[] = [
  "sow",
  "proposal",
  "change_order",
  "purchase_order",
  "extension",
];

const ENGAGEMENT_TAG_FALLBACK_LABELS: Readonly<Record<string, string>> = {
  sow: "SOW",
  proposal: "Proposal",
  change_order: "Change Order",
  purchase_order: "Purchase Order",
  extension: "Extension",
};

export const ALL_DOCUMENTS_FILTER = "all";

const FOLDER_PATH_SEPARATOR = "/";
const ROOT_FOLDER_LABEL = "All Files";
const DISPLAY_PATH_SEPARATOR = " / ";

export const isEngagementDocumentTagId = (
  documentType: string | null | undefined,
): boolean =>
  !!documentType && ENGAGEMENT_DOCUMENT_TAG_IDS.includes(documentType);

export const getDocumentTypeLabel = (
  documentType: string | null | undefined,
  tags: IContextFileTag[],
): string | null => {
  if (!documentType) return null;
  const matchingTag = tags.find((tag) => tag.id === documentType);
  return (
    matchingTag?.name || ENGAGEMENT_TAG_FALLBACK_LABELS[documentType] || null
  );
};

export const getFolderSegments = (filename: string): string[] =>
  filename.split(FOLDER_PATH_SEPARATOR).slice(0, -1).filter(Boolean);

export const getPickerFolderLabel = (filename: string): string =>
  getFolderSegments(filename).join(DISPLAY_PATH_SEPARATOR);

export const getFullFolderPathLabel = (filename: string): string =>
  [ROOT_FOLDER_LABEL, ...getFolderSegments(filename)].join(
    DISPLAY_PATH_SEPARATOR,
  );

export const countDocumentsByTagId = (
  documents: IEngagementSourceDocument[],
): Record<string, number> =>
  documents.reduce<Record<string, number>>((counts, sourceDocument) => {
    if (sourceDocument.documentType) {
      counts[sourceDocument.documentType] =
        (counts[sourceDocument.documentType] ?? 0) + 1;
    }
    return counts;
  }, {});

export const getTagIdsWithDocuments = (
  tagCounts: Record<string, number>,
  tags: IContextFileTag[],
): string[] => {
  const engagementTagIds = ENGAGEMENT_DOCUMENT_TAG_IDS.filter(
    (tagId) => (tagCounts[tagId] ?? 0) > 0,
  );
  const otherTagIds = Object.keys(tagCounts)
    .filter(
      (tagId) =>
        tagCounts[tagId] > 0 &&
        !ENGAGEMENT_DOCUMENT_TAG_IDS.includes(tagId) &&
        tags.some((tag) => tag.id === tagId),
    )
    .sort((firstTagId, secondTagId) =>
      (getDocumentTypeLabel(firstTagId, tags) ?? "").localeCompare(
        getDocumentTypeLabel(secondTagId, tags) ?? "",
      ),
    );
  return [...engagementTagIds, ...otherTagIds];
};

export const filterSourceDocuments = (
  documents: IEngagementSourceDocument[],
  searchQuery: string,
  tagFilter: string,
): IEngagementSourceDocument[] => {
  const normalizedQuery = searchQuery.trim().toLowerCase();
  return documents.filter((sourceDocument) => {
    const matchesTag =
      tagFilter === ALL_DOCUMENTS_FILTER ||
      sourceDocument.documentType === tagFilter;
    const matchesSearch =
      normalizedQuery === "" ||
      sourceDocument.originalFilename.toLowerCase().includes(normalizedQuery);
    return matchesTag && matchesSearch;
  });
};

export interface IGroupedSourceDocuments {
  engagementDocuments: IEngagementSourceDocument[];
  otherDocuments: IEngagementSourceDocument[];
}

export const groupSourceDocuments = (
  documents: IEngagementSourceDocument[],
): IGroupedSourceDocuments => ({
  engagementDocuments: documents.filter((sourceDocument) =>
    isEngagementDocumentTagId(sourceDocument.documentType),
  ),
  otherDocuments: documents.filter(
    (sourceDocument) => !isEngagementDocumentTagId(sourceDocument.documentType),
  ),
});
